import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useWishlist } from "../contexts/WishlistContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import ScarcityBadge from "../components/ui/ScarcityBadge";
import DynamicPriceDisplay from "../components/ui/DynamicPriceDisplay";
import { Heart, ShoppingCart, Trash2, Package } from "lucide-react";

const WishlistPage = () => {
  const { wishlistItems, loading, removeFromWishlist, moveToCart } = useWishlist();
  const [removing, setRemoving] = useState({});
  const [movingToCart, setMovingToCart] = useState({});

  const handleRemove = async (productId) => {
    setRemoving(prev => ({ ...prev, [productId]: true }));
    await removeFromWishlist(productId);
    setRemoving(prev => ({ ...prev, [productId]: false }));
  };

  const handleMoveToCart = async (productId) => {
    setMovingToCart(prev => ({ ...prev, [productId]: true }));
    await moveToCart(productId);
    setMovingToCart(prev => ({ ...prev, [productId]: false }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price || 0);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="wishlist-page">
      <Navigation />

      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/20 to-red-500/20">
              <Heart className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold">My Wishlist</h1>
              <p className="text-muted-foreground">
                {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'} saved
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-muted rounded-2xl mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : wishlistItems.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Heart className="w-8 h-8 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-medium mb-2">Your wishlist is empty</h2>
                <p className="text-muted-foreground mb-6">
                  Save items you love by clicking the heart icon
                </p>
                <Link to="/products">
                  <Button className="rounded-full">
                    <Package className="w-4 h-4 mr-2" />
                    Browse Collection
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {wishlistItems.map((product) => (
                  <motion.div
                    key={product.product_id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card className="overflow-hidden group">
                      <Link to={`/products/${product.product_id}`}>
                        <div className="relative aspect-[4/5] overflow-hidden">
                          <img
                            src={product.images?.[0] || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                            alt={product.title}
                            className="w-full h-full object-contain bg-transparent transition-transform duration-500 group-hover:scale-105"
                          />
                          
                          {/* Badges */}
                          <div className="absolute top-3 left-3 flex flex-col gap-2">
                            {product.is_limited_edition && (
                              <span className="px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded">
                                Limited Edition
                              </span>
                            )}
                            {product.dynamic_pricing?.scarcity_label && (
                              <ScarcityBadge 
                                label={product.dynamic_pricing.scarcity_label}
                                stockPercent={product.dynamic_pricing.stock_percent}
                              />
                            )}
                          </div>
                        </div>
                      </Link>

                      <CardContent className="p-4">
                        <Link to={`/products/${product.product_id}`}>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                            {product.artist?.name || "Artist"}
                          </p>
                          <h3 className="font-medium mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                            {product.title}
                          </h3>
                        </Link>

                        {/* Price */}
                        {product.dynamic_pricing ? (
                          <DynamicPriceDisplay 
                            dynamicPricing={product.dynamic_pricing}
                            size="sm"
                            showOriginal={true}
                          />
                        ) : (
                          <p className="font-medium">{formatPrice(product.price)}</p>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 mt-4">
                          <Button
                            className="flex-1 rounded-full"
                            onClick={() => handleMoveToCart(product.product_id)}
                            disabled={movingToCart[product.product_id]}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            {movingToCart[product.product_id] ? "Adding..." : "Add to Cart"}
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full"
                            onClick={() => handleRemove(product.product_id)}
                            disabled={removing[product.product_id]}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WishlistPage;
